import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TestimonialsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: 'احمد محمدی',
      role: 'کارآفرین',
      content:
        'ایران‌داد کمک شایانی به من در درک قراردادهای تجاری کرد. پاسخ‌ها دقیق و قابل فهم بودند.',
      rating: 5,
      avatar: 'User',
    },
    {
      id: 2,
      name: 'فاطمه احمدی',
      role: 'وکیل دادگستری',
      content:
        'به عنوان یک وکیل، از کیفیت پاسخ‌های این سیستم تعجب کردم. ابزار مفیدی برای مشاوره اولیه است.',
      rating: 5,
      avatar: 'UserCheck',
    },
    {
      id: 3,
      name: 'علی رضایی',
      role: 'صاحب کسب و کار',
      content:
        'زمان زیادی از من صرفه‌جویی شد. پیش از مراجعه به وکیل، سوالات اولیه‌ام را از ربات پرسیدم.',
      rating: 5,
      avatar: 'Briefcase',
    },
    {
      id: 4,
      name: 'مریم کریمی',
      role: 'مدیر منابع انسانی',
      content:
        'در زمینه قوانین کار سوالات زیادی داشتم که همگی به صورت دقیق و سریع پاسخ داده شدند.',
      rating: 5,
      avatar: 'Users',
    },
  ];

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials?.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials?.length);
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prevIndex) =>
        (prevIndex - 1 + testimonials?.length) % testimonials?.length
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={`${
          index < rating ? 'text-accent fill-accent' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            نظر کاربران
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            تجربه کاربران ما از استفاده از سرویس مشاوره حقوقی هوشمند
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div
          className="relative max-w-4xl mx-auto"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}>
          {/* Main Testimonial */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="bg-card rounded-3xl p-8 lg:p-12 shadow-moderate border border-border">
              <div className="text-center">
                {/* Avatar */}
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon
                    name={testimonials?.[currentIndex]?.avatar}
                    size={32}
                    color="white"
                  />
                </div>

                {/* Rating */}
                <div className="flex items-center justify-center gap-1 mb-6">
                  {renderStars(testimonials?.[currentIndex]?.rating)}
                </div>

                {/* Content */}
                <blockquote className="text-lg lg:text-xl text-foreground mb-8 leading-relaxed italic">
                  "{testimonials?.[currentIndex]?.content}"
                </blockquote>

                {/* Author */}
                <div className="space-y-1">
                  <h4 className="text-lg font-semibold text-foreground">
                    {testimonials?.[currentIndex]?.name}
                  </h4>
                  <p className="text-muted-foreground">
                    {testimonials?.[currentIndex]?.role}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Arrows */}
          <div className="absolute top-1/2 transform -translate-y-1/2 -left-4 -right-4 flex justify-between pointer-events-none">
            <button
              onClick={prevSlide}
              className="w-12 h-12 bg-background border border-border rounded-full flex items-center justify-center shadow-moderate hover:shadow-lg transition-all duration-300 hover:border-primary pointer-events-auto focus-ring"
              aria-label="Previous testimonial">
              <Icon name="ChevronRight" size={20} className="text-foreground" />
            </button>
            <button
              onClick={nextSlide}
              className="w-12 h-12 bg-background border border-border rounded-full flex items-center justify-center shadow-moderate hover:shadow-lg transition-all duration-300 hover:border-primary pointer-events-auto focus-ring"
              aria-label="Next testimonial">
              <Icon name="ChevronLeft" size={20} className="text-foreground" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials?.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 focus-ring ${
                  index === currentIndex
                    ? 'bg-primary scale-125'
                    : 'bg-border hover:bg-primary/50'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          {/* Progress Bar */}
          <div className="mt-6 w-full bg-border rounded-full h-1 overflow-hidden">
            <motion.div
              className="h-full bg-primary rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 5, ease: 'linear' }}
              key={currentIndex}
            />
          </div>
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-16 flex flex-wrap justify-center gap-8 items-center opacity-60">
          <div className="flex items-center gap-2">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="text-sm text-muted-foreground">تایید شده</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Award" size={16} className="text-accent" />
            <span className="text-sm text-muted-foreground">
              برنده جایزه نوآوری
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Users" size={16} className="text-primary" />
            <span className="text-sm text-muted-foreground">
              اعتماد هزاران کاربر
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
